/**
 * §07 — Enemy archetypes, spawn/init from content defs, per-archetype attack state
 * machines, elite + affix logic. Sim step 4.
 *
 * Determinism: every roll here consumes `w.rng.ai` (behaviour) or `w.rng.spawn`
 * (spawn-time draws). Zero allocation per tick: all per-enemy auxiliary state lives
 * in module-scope typed arrays keyed by `pool_index`, and every def is flattened into
 * a runtime record once at module load.
 */

import { cfg } from '../core/config';
import { ELITE_MODIFIER, AFFIXES, ENEMIES, difficultyById, type EnemyDef } from '../core/content';
import type { Enemy, Projectile } from './entities';
import { POOL_CAPACITY, type World } from './world';
import {
  beginSteerFrame,
  resolveSeparation,
  steerEnemy,
  applyTurnLimit,
  NEIGHBOUR_CAP,
} from './steering';
import { wrapDeltaX, wrapDeltaY, wrapPos, wrapX, wrapY } from './wrap';

export { assignFormation, formationCount, formationX, formationY, formationDelayTicks, formationAngle } from './formations';

const STEP = 1 / 60;
const DEG = Math.PI / 180;
const TAU = Math.PI * 2;

/** ms → ticks at the fixed 60 Hz step. */
function ticksOf(ms: number): number {
  return Math.round(ms * 0.06);
}

/** §11.9 — anything that can damage the player winds up for at least 300 ms. */
export const MIN_DAMAGING_TELEGRAPH_MS = 300;

/** §7.2 growth curves. HP × 1.6^(t/120); contact damage × 1.08^(t/120) capped at ×2. */
export const HP_GROWTH_BASE = 1.6;
export const DMG_GROWTH_BASE = 1.08;
export const DMG_GROWTH_CAP = 2.0;
export const GROWTH_PERIOD_S = 120;

/** §8.10 anti-stall ramp. */
export const STALL_RAMP_START_S = 45;
export const STALL_RAMP_PER_S = 0.015;
export const STALL_RAMP_CAP = 0.4;
export const STALL_TERMINATE_S = 90;
/** Camera-view proxy for the §8.10 "no off-screen deaths" rule. */
export const IN_VIEW_RADIUS_U = 30;

/**
 * §8.10 hunt ramp — the anti-stall rule restated in the unit a torus needs (AMD-24c).
 *
 * Two things were wrong with the rule as coded, and both are consequences of AMD-24.
 *
 * **The cap is in the wrong unit.** `STALL_RAMP_CAP` is a fraction of the archetype's own
 * speed. On a bounded arena that was enough because the wall, not the AI, ended the chase.
 * On a torus a straight line has no end, so "+40% of 5.2" is 7.28 against a player holding
 * 8.0, and the ramp cannot reach its own stated goal for any trash archetype under 5.72
 * u/s — which is eight of the twelve mobile ones. The hunt ramp closes the *deficit* instead: it
 * ramps toward a hair over the player's own top speed, whatever the archetype started at.
 *
 * **The trigger has to be a property of the player's path, not of each enemy.** The
 * obvious per-enemy trigger — "this body has failed to reach the player for N seconds" —
 * fires for most of the field in *every* playstyle, because most enemies die at range long
 * before they arrive. Measured on the patrol autopilot: mean 9.5 alive, mean 1.06 of them
 * inside 12 u, so a per-enemy proximity counter marks roughly a third of the field as
 * stalled during ordinary engaged play. The thing that actually differs is the shape of
 * the player's path. Fleeing is a straight line; fighting is a loop. So the ramp keys off
 * the straightness of the last {@link FLEE_WINDOW_S} seconds of travel, measured on the
 * torus — and that is what makes it self-limiting: turn, and it decays.
 *
 * **AMD-46 — the second pole.** Straightness alone reads a planted ship as "not fleeing", so
 * a stander registers as nothing worth committing to and the swarm trickles. Measured on the
 * §9.13 stillness protocol before this change: mean 4.2 bodies alive, 0.66 of them inside the
 * 18 u acquisition ring, 0.24 inside 12 u. That is not a line being held, it is a ship being
 * ignored — and because §9.13's payout is gated on ring weight, an ignored stander also earns
 * nothing, which is how the whole family failed at 3.38 min against 10.48 for a patrolling
 * pilot. Straightness and stillness are the two ways a player denies the swarm a fight, so
 * {@link currentHuntPressure} is the max of the two: a straight sprint, or an anchor held for
 * {@link ANCHOR_WINDOW_S} seconds. Both self-limit on the same property — move normally and
 * both terms fall away — and the anchor term uses the same still/moving band as the §9.13.2
 * meter, so one rule drives the meter and the pressure it is paid for surviving.
 */
/** Floor on the "it has arrived" radius; archetypes with a range or stalk band use theirs. */
export const HUNT_REACH_RADIUS = 10;
/** Rolling window over which the player's path straightness is measured. */
export const FLEE_WINDOW_S = 20;
/**
 * Straightness below which nothing ramps, and at which it is fully ramped. Straightness is
 * net displacement over the window divided by the distance a player at top speed would
 * cover, so 1.0 is a dead-straight sprint and 0 is a closed loop. The patrol autopilot's
 * figure-eight measures 0.28 mean / 0.72 peak; the §07 outrun line measures 0.99.
 */
export const FLEE_LOW = 0.80;
export const FLEE_HIGH = 0.95;
/**
 * Lookback for the anchor term (AMD-46). Shorter than the flee window on purpose: fleeing is
 * a sustained commitment that takes a while to prove, while planting is instant and visible,
 * and the §9.13.2 meter it shares a band with fills in 2 s. Five seconds is long enough that
 * a dodge, a knockback or a dash-and-return does not arm it.
 */
export const ANCHOR_WINDOW_S = 5;
/**
 * Path length travelled over {@link ANCHOR_WINDOW_S}, as a fraction of the distance a player
 * at top speed would have covered. At or below `ANCHOR_STILL` the ship reads as planted; at
 * or above `ANCHOR_MOVE` it reads as under way. These are `hold.still_speed_u` (0.8 u/s) and
 * `hold.move_speed_u` (2.4 u/s) expressed against the 8 u/s top speed, so the swarm commits
 * on exactly the band that fills the Holdfast meter — one rule, two consequences.
 */
export const ANCHOR_STILL = 0.10;
export const ANCHOR_MOVE = 0.30;
/**
 * Station-keeping floor for a hunter that has arrived while the player is still fleeing, as
 * a fraction of the player's top speed. Without it the ramp is a revolving door: the hunter
 * crosses into range, drops back to its archetype speed, and slides out again having landed
 * one graze.
 */
export const HUNT_HOLD = 1.02;
/** Full-hunt ceiling as a fraction of the player's own top speed. */
export const HUNT_OVERTAKE = 1.25;
/** §4.2's cap on `move_speed_mult`, mirrored so a speed build cannot outrun the ceiling. */
export const HUNT_MOVE_MULT_CAP = 1.4;

/** Birth/death animation windows driven here; R1's lifecycle releases the slot. */
export const DEATH_ANIM_TICKS = 21;

/** Affix bitmask (module-local; content ids stay the contract). */
export const AFFIX_SHIELDED = 1;
export const AFFIX_VOLATILE = 2;
export const AFFIX_SWIFT = 4;
export const AFFIX_WARDEN = 8;
export const AFFIX_BRISTLING = 16;

const AFFIX_IDS = ['affix.shielded', 'affix.volatile', 'affix.swift', 'affix.warden', 'affix.bristling'] as const;

/** Shield provenance so §7.5 warden and §7.2.9 aegis bubbles never stack. */
const SHIELD_NONE = 0;
const SHIELD_AFFIX = 1;
const SHIELD_WARDEN = 2;
const SHIELD_AEGIS = 3;

/** attack_state values (presentation contract, INTERFACES.md). */
export const STATE_IDLE = 0;
export const STATE_WINDING = 1;
export const STATE_COMMITTED = 2;
export const STATE_RECOVERING = 3;

/** Attack machines. */
const ATK_CONTACT = 0;
const ATK_PROJECTILE = 1;
const ATK_LOBBED = 2;
const ATK_CHARGE = 3;
const ATK_ZONE = 4;
const ATK_TETHER = 5;
const ATK_AMBUSH = 6;
const ATK_MELEE = 7;
const ATK_SUPPORT = 8;
const ATK_BIRTH = 9;
const ATK_BLOOM_BEAT = 10;
const ATK_SPLIT = 11;

/**
 * Projectile visual classes for enemy-fired shapes. §14.2 reserves 12 player classes
 * (0–11); enemy classes continue from 12. R6 maps these in visual-classes.ts.
 */
export const VISUAL_ENEMY_BARB = 12;
export const VISUAL_ENEMY_GLOB = 13;
export const VISUAL_ENEMY_DART = 14;

/**
 * Enemy-fired shapes use behaviour ids past R2's `Behaviour` enum (0–11) so
 * `stepProjectiles` falls through its `default` branch and leaves them to this
 * module. R6 keys visuals off `visual_class`, which is unaffected.
 */
export const BEHAVIOUR_ENEMY_LINEAR = 20;
export const BEHAVIOUR_ENEMY_LOBBED = 21;

/** Hazard id seeded by `enemy.nettle-anemone`, and the affix.volatile ring telegraph. */
export const HAZARD_NETTLE_ZONE = 'hazard.nettle-zone';
export const HAZARD_VOLATILE_RING = 'hazard.volatile-ring';

/** Synthetic child archetype from `enemy.bloom-jelly.split.child` (§7.2.5). */
export const BLOOMLING_ID = 'enemy.bloomling';

/**
 * Hitbox radii are not in the content tables; §12 silhouette sizes are mapped here.
 * Module-local by design — a shared config key would be owned by another role.
 */
const RADIUS_BY_ID: Record<string, number> = {
  'enemy.driftfin': 0.55,
  'enemy.glimmerfry': 0.35,
  'enemy.dart-eel': 0.5,
  'enemy.ventspitter': 0.6,
  'enemy.bloom-jelly': 0.7,
  'enemy.bloomling': 0.4,
  'enemy.basalt-crab': 0.9,
  'enemy.spearnose': 0.65,
  'enemy.nettle-anemone': 0.8,
  'enemy.aegis-medusa': 0.85,
  'enemy.siphon-leech': 0.5,
  'enemy.gloomsquid': 0.7,
  'enemy.luremaw': 1.1,
  'enemy.colonial-curtain': 0.8,
  'enemy.brood-bell': 1.3,
};

/** Cadences the GDD leaves open; module-local defaults, listed in the build report. */
const GLOOMSQUID_CADENCE_S = 2.5;
const GLOOMSQUID_MAX_RANGE_U = 18;
const LUREMAW_LUNGE_S = 1.0;
const TETHER_BREAK_RANGE_U = 10;
const TETHER_BREAK_COOLDOWN_S = 5;
const SURGE_DRAIN_PERIOD_S = 2;
const CLAW_COOLDOWN_S = 2.0;
const BITE_COOLDOWN_S = 0.5;
const CURTAIN_DRIFT_ROT_DEG_S = 6;

interface DefRuntime {
  id: string;
  index: number;
  def: EnemyDef | null;
  hpBase: number;
  contactBase: number;
  moveSpeed: number;
  maxForce: number;
  turnRate: number; // rad/s
  radius: number;
  threat: number;
  xpMotes: number;
  popBudget: number;
  wSep: number;
  wAli: number;
  wCoh: number;
  wPur: number;
  wAvo: number;
  wForm: number;
  pursuitLead: number;
  bandMin: number;
  bandMax: number;
  /** §8.10 — the range this archetype is trying to reach. 0 means it never hunts. */
  huntReach: number;
  stationary: boolean;
  scripted: boolean;
  atk: number;
  telegraphTicks: number;
  // ranged
  projDamage: number;
  projSpeed: number;
  projRange: number;
  cadenceTicks: number;
  // melee
  meleeRange: number;
  meleeDamage: number;
  // armor
  armorArcDeg: number;
  armorReduction: number;
  // charge
  chargeSpeed: number;
  chargeTurn: number;
  chargeTicks: number;
  chargeDamage: number;
  chargeCorrectionRad: number;
  recoveryTicks: number;
  recoveryDamageMult: number;
  stalkMin: number;
  stalkMax: number;
  stalkDurMinTicks: number;
  stalkDurMaxTicks: number;
  // zone
  zoneRadius: number;
  zoneDps: number;
  zoneActiveTicks: number;
  zoneCadenceTicks: number;
  zoneCap: number;
  // tether
  tetherRange: number;
  tetherTicks: number;
  tetherDps: number;
  // ambush
  ambushTrigger: number;
  lungeSpeed: number;
  lungeTurn: number;
  biteDamage: number;
  biteArc: number;
  reburyTicks: number;
  // birth
  birthId: string;
  birthCount: number;
  birthCadenceTicks: number;
  birthMaxChildren: number;
  // support
  supportPool: number;
  supportRegen: number;
  supportMax: number;
  supportCadenceTicks: number;
  supportRange: number;
  // colony
  bloomBeatTicks: number;
  bloomAura: number;
  // split
  splitCount: number;
  splitFanRad: number;
}

function numOf(src: unknown, key: string, dflt: number): number {
  if (src === null || typeof src !== 'object') return dflt;
  const v = (src as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : dflt;
}

function objOf(src: unknown, key: string): unknown {
  if (src === null || typeof src !== 'object') return null;
  const v = (src as Record<string, unknown>)[key];
  return v !== null && typeof v === 'object' ? v : null;
}

function strOf(src: unknown, key: string, dflt: string): string {
  if (src === null || typeof src !== 'object') return dflt;
  const v = (src as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : dflt;
}

function atkKindOf(def: EnemyDef): number {
  switch (def.attack.type) {
    case 'projectile':
      return ATK_PROJECTILE;
    case 'lobbed_projectile':
      return ATK_LOBBED;
    case 'charge':
      return ATK_CHARGE;
    case 'zone_seed':
      return ATK_ZONE;
    case 'tether':
      return ATK_TETHER;
    case 'ambush_lunge':
      return ATK_AMBUSH;
    default:
      break;
  }
  if (def.id === 'enemy.basalt-crab') return ATK_MELEE;
  if (def.id === 'enemy.aegis-medusa') return ATK_SUPPORT;
  if (def.id === 'enemy.brood-bell') return ATK_BIRTH;
  if (def.id === 'enemy.colonial-curtain') return ATK_BLOOM_BEAT;
  if (def.id === 'enemy.bloom-jelly') return ATK_SPLIT;
  return ATK_CONTACT;
}

function blank(id: string, index: number): DefRuntime {
  return {
    id,
    index,
    def: null,
    hpBase: 1,
    contactBase: 0,
    moveSpeed: 0,
    maxForce: 0,
    turnRate: 0,
    radius: 0.5,
    threat: 0,
    xpMotes: 0,
    popBudget: 999,
    wSep: 0,
    wAli: 0,
    wCoh: 0,
    wPur: 0,
    wAvo: 0,
    wForm: 0,
    pursuitLead: 0,
    bandMin: 0,
    bandMax: 0,
    huntReach: 0,
    stationary: false,
    scripted: false,
    atk: ATK_CONTACT,
    telegraphTicks: 0,
    projDamage: 0,
    projSpeed: 0,
    projRange: 0,
    cadenceTicks: 0,
    meleeRange: 0,
    meleeDamage: 0,
    armorArcDeg: 0,
    armorReduction: 0,
    chargeSpeed: 0,
    chargeTurn: 0,
    chargeTicks: 0,
    chargeDamage: 0,
    chargeCorrectionRad: 0,
    recoveryTicks: 0,
    recoveryDamageMult: 1,
    stalkMin: 0,
    stalkMax: 0,
    stalkDurMinTicks: 0,
    stalkDurMaxTicks: 0,
    zoneRadius: 0,
    zoneDps: 0,
    zoneActiveTicks: 0,
    zoneCadenceTicks: 0,
    zoneCap: 6,
    tetherRange: 0,
    tetherTicks: 0,
    tetherDps: 0,
    ambushTrigger: 0,
    lungeSpeed: 0,
    lungeTurn: 0,
    biteDamage: 0,
    biteArc: 0,
    reburyTicks: 0,
    birthId: '',
    birthCount: 0,
    birthCadenceTicks: 0,
    birthMaxChildren: 0,
    supportPool: 0,
    supportRegen: 0,
    supportMax: 0,
    supportCadenceTicks: 0,
    supportRange: 0,
    bloomBeatTicks: 0,
    bloomAura: 0,
    splitCount: 0,
    splitFanRad: 0,
  };
}

function buildRuntime(def: EnemyDef, index: number): DefRuntime {
  const r = blank(def.id, index);
  r.def = def;
  r.hpBase = def.stats.hp_base;
  r.contactBase = def.stats.contact_damage_base;
  r.moveSpeed = def.stats.move_speed;
  r.maxForce = def.steering.max_force;
  r.turnRate = def.steering.turn_rate_deg_s * DEG;
  r.radius = RADIUS_BY_ID[def.id] ?? 0.6;
  r.threat = def.threat_score;
  r.xpMotes = numOf(def.drops, 'xp_motes', numOf(def.drops, 'xp_motes_avg', 1));
  r.popBudget = def.pop_budget;
  r.wSep = def.steering.weights.separation;
  r.wAli = def.steering.weights.alignment;
  r.wCoh = def.steering.weights.cohesion;
  r.wPur = def.steering.weights.pursuit;
  r.wAvo = def.steering.weights.avoidance;
  r.wForm = def.steering.weights.formation;
  r.pursuitLead = numOf(def.steering, 'pursuit_prediction_s', 0);
  const band = (def.steering as Record<string, unknown>)['range_band_u'];
  if (Array.isArray(band) && band.length === 2) {
    r.bandMin = Number(band[0]);
    r.bandMax = Number(band[1]);
  }
  r.stationary = r.moveSpeed <= 0;
  r.scripted = def.steering.scripted === true;
  r.atk = atkKindOf(def);
  r.telegraphTicks = ticksOf(def.telegraph.duration_ms);

  const atk = def.attack as Record<string, unknown>;
  r.projDamage = numOf(atk, 'projectile_damage', 0);
  r.projSpeed = numOf(atk, 'projectile_speed', numOf(atk, 'flight_speed', 0));
  r.projRange = numOf(atk, 'max_range_u', 0);
  const cadence = numOf(atk, 'cadence_s', 0);
  r.cadenceTicks = cadence > 0 ? Math.round(cadence * 60) : 0;

  const melee = objOf(def.attack, 'melee');
  if (melee) {
    r.meleeRange = numOf(melee, 'range_u', 0);
    r.meleeDamage = numOf(melee, 'damage', 0);
  }

  const armor = objOf(def, 'armor');
  if (armor) {
    r.armorArcDeg = numOf(armor, 'frontal_arc_deg', 0);
    r.armorReduction = numOf(armor, 'reduction', 0);
  }

  const charge = objOf(def, 'charge_state');
  if (charge) {
    r.chargeSpeed = numOf(charge, 'speed', 0);
    r.chargeTurn = numOf(charge, 'turn_rate_deg_s', 0) * DEG;
    r.chargeTicks = Math.round(numOf(charge, 'duration_s', 0) * 60);
    r.chargeDamage = numOf(charge, 'damage', 0);
    r.chargeCorrectionRad = numOf(charge, 'max_heading_correction_deg', 0) * DEG;
    r.recoveryTicks = Math.round(numOf(charge, 'recovery_s', 0) * 60);
    r.recoveryDamageMult = numOf(charge, 'recovery_damage_taken_mult', 1);
    const sb = atk['stalk_band_u'];
    if (Array.isArray(sb) && sb.length === 2) {
      r.stalkMin = Number(sb[0]);
      r.stalkMax = Number(sb[1]);
    }
    const sd = atk['stalk_duration_s'];
    if (Array.isArray(sd) && sd.length === 2) {
      r.stalkDurMinTicks = Math.round(Number(sd[0]) * 60);
      r.stalkDurMaxTicks = Math.round(Number(sd[1]) * 60);
    }
  }

  const zone = objOf(def, 'zone');
  if (zone) {
    r.zoneRadius = numOf(zone, 'radius_u', 0);
    r.zoneDps = numOf(zone, 'dps', 0);
    r.zoneActiveTicks = Math.round(numOf(zone, 'active_s', 0) * 60);
    r.zoneCadenceTicks = Math.round(numOf(zone, 'reseed_cadence_s', 0) * 60);
    r.zoneCap = numOf(zone, 'global_cap', 6);
  }

  const tether = objOf(def, 'tether');
  if (tether) {
    r.tetherRange = numOf(tether, 'range_u', 0);
    r.tetherTicks = Math.round(numOf(tether, 'attach_s', 0) * 60);
    r.tetherDps = numOf(tether, 'drain_dps', 0);
  }

  const ambush = objOf(def, 'ambush');
  if (ambush) {
    r.ambushTrigger = numOf(ambush, 'trigger_radius_u', 0);
    r.lungeSpeed = numOf(ambush, 'lunge_speed', 0);
    r.lungeTurn = numOf(ambush, 'lunge_turn_clamp_deg_s', 0) * DEG;
    r.biteDamage = numOf(ambush, 'bite_damage', 0);
    r.biteArc = numOf(ambush, 'bite_arc_u', 0);
    r.reburyTicks = Math.round(numOf(ambush, 'rebury_s', 0) * 60);
    r.recoveryDamageMult = numOf(ambush, 'rebury_damage_taken_mult', 1);
  }

  const birth = objOf(def, 'birth');
  if (birth) {
    r.birthId = strOf(birth, 'spawn_id', '');
    r.birthCount = numOf(birth, 'count', 0);
    r.birthCadenceTicks = Math.round(numOf(birth, 'cadence_s', 0) * 60);
    r.birthMaxChildren = numOf(birth, 'max_live_children', 0);
  }

  const support = objOf(def, 'support');
  if (support) {
    r.supportPool = numOf(support, 'shield_pool', 0);
    r.supportRegen = numOf(support, 'shield_regen_per_s', 0);
    r.supportMax = numOf(support, 'max_bubbles', 0);
    r.supportCadenceTicks = Math.round(numOf(support, 'recast_cadence_s', 0) * 60);
    r.supportRange = 8;
  }

  const colony = objOf(def, 'colony');
  if (colony) {
    r.bloomBeatTicks = Math.round(numOf(colony, 'bloom_beat_s', 0) * 60);
    r.bloomAura = numOf(colony, 'bloom_aura_u', 0);
  }

  const split = objOf(def, 'split');
  if (split) {
    r.splitCount = numOf(split, 'on_death_count', 0);
    r.splitFanRad = numOf(objOf(split, 'child'), 'fan_deg', 120) * DEG;
  }

  if (r.id === 'enemy.gloomsquid') {
    r.cadenceTicks = Math.round(GLOOMSQUID_CADENCE_S * 60);
    r.projRange = GLOOMSQUID_MAX_RANGE_U;
  }
  return r;
}

/** Runtime def table: content order, plus the synthetic bloomling at the end. */
const DEFS: DefRuntime[] = [];
const DEF_INDEX = new Map<string, number>();
(function buildDefs(): void {
  for (let i = 0; i < ENEMIES.length; i++) {
    const rt = buildRuntime(ENEMIES[i], i);
    DEF_INDEX.set(rt.id, DEFS.length);
    DEFS.push(rt);
  }
  const jellyIdx = DEF_INDEX.get('enemy.bloom-jelly');
  const jelly = jellyIdx === undefined ? null : DEFS[jellyIdx].def;
  const child = jelly ? objOf(jelly, 'split') : null;
  const c = child ? objOf(child, 'child') : null;
  const rt = blank(BLOOMLING_ID, DEFS.length);
  rt.hpBase = numOf(c, 'hp_base', 6);
  rt.contactBase = numOf(c, 'contact_damage_base', 5);
  rt.moveSpeed = numOf(c, 'move_speed', 5.5);
  rt.turnRate = numOf(c, 'turn_rate_deg_s', 380) * DEG;
  rt.maxForce = 22;
  rt.radius = RADIUS_BY_ID[BLOOMLING_ID];
  rt.threat = numOf(c, 'threat_cost', 0);
  rt.xpMotes = 1;
  rt.popBudget = 240;
  rt.wSep = 1.0;
  rt.wAli = 0.3;
  rt.wCoh = 0.4;
  rt.wPur = 0.9;
  rt.wAvo = 1.0;
  rt.wForm = 0.5;
  rt.atk = ATK_CONTACT;
  rt.telegraphTicks = ticksOf(300);
  DEF_INDEX.set(BLOOMLING_ID, DEFS.length);
  DEFS.push(rt);

  // §8.10 — one place, so the synthetic bloomling is covered by the same rule as content.
  // "Reached" means reached *its own* engagement range: a ventspitter that has made its
  // firing band has arrived, and hunting it further would delete its §7 range identity.
  for (let i = 0; i < DEFS.length; i++) {
    const r = DEFS[i];
    if (r.stationary || r.moveSpeed <= 0 || r.wPur <= 0) continue;
    let reach = HUNT_REACH_RADIUS;
    if (r.bandMax > reach) reach = r.bandMax;
    if (r.stalkMax > reach) reach = r.stalkMax;
    if (r.meleeRange > reach) reach = r.meleeRange;
    if (r.supportRange > reach) reach = r.supportRange;
    r.huntReach = reach;
  }
})();

export function enemyDefCount(): number {
  return DEFS.length;
}

export function enemyDefIndex(defId: string): number {
  const i = DEF_INDEX.get(defId);
  return i === undefined ? -1 : i;
}

// --- per-slot auxiliary state (SoA, keyed by pool_index) ---

const CAP = POOL_CAPACITY.enemies;
const sDef = new Int32Array(CAP);
const sAffix = new Uint8Array(CAP);
const sSpeed = new Float64Array(CAP);
const sForce = new Float64Array(CAP);
const sTurn = new Float64Array(CAP);
const sContact = new Float64Array(CAP);
const sShieldPool = new Float64Array(CAP);
const sShieldSrc = new Uint8Array(CAP);
const sPrevShield = new Float64Array(CAP);
const sPrevHp = new Float64Array(CAP);
const sStateEnd = new Int32Array(CAP);
const sNextAction = new Int32Array(CAP);
const sBristleReady = new Int32Array(CAP);
const sWardenNext = new Int32Array(CAP);
const sChildren = new Int32Array(CAP);
const sParent = new Int32Array(CAP);
const sZoneOwned = new Int32Array(CAP);
const sTetherNext = new Int32Array(CAP);
const sSurgeNext = new Int32Array(CAP);
const sChargeYaw = new Float64Array(CAP);
const sGoalAngle = new Float64Array(CAP);
const sDeathHandled = new Uint8Array(CAP);
const sMaterialized = new Uint8Array(CAP);
const sDriftRot = new Float64Array(CAP);

/** Live zone count for the §8.4 area-denial cap. */
let liveZones = 0;

/** Enemy-owned projectiles when R2's spawner is not installed. */
const ownedProj = new Uint8Array(POOL_CAPACITY.projectiles);
let ownsProjectiles = true;

// --- deferred bursts (affix.volatile ring, affix.bristling darts) ---

const BURST_CAP = 64;
const bX = new Float64Array(BURST_CAP);
const bY = new Float64Array(BURST_CAP);
const bR = new Float64Array(BURST_CAP);
const bDmg = new Float64Array(BURST_CAP);
const bTick = new Int32Array(BURST_CAP);
const bKind = new Uint8Array(BURST_CAP);
const bOwner = new Int32Array(BURST_CAP);
let burstCount = 0;

const BURST_RING = 0;
const BURST_DARTS = 1;

/** §7.5 affix.volatile — 600 ms telegraph, 15 dmg in 3.5 u. */
export const VOLATILE_TELEGRAPH_MS = 600;
export const VOLATILE_RADIUS_U = 3.5;
export const VOLATILE_DAMAGE = 15;
/** §7.5 affix.bristling — 20% on hit via `ai`, 1.5 s min interval, 300 ms glow, 3 darts. */
export const BRISTLING_CHANCE = 0.2;
export const BRISTLING_INTERVAL_S = 1.5;
export const BRISTLING_TELEGRAPH_MS = 300;
export const BRISTLING_DARTS = 3;
export const BRISTLING_DART_SPEED = 7;
export const BRISTLING_DART_DAMAGE = 5;
/** §7.5 affix.warden — 20 HP ally shield in 5 u, refreshed every 4 s. */
export const WARDEN_RADIUS_U = 5;
export const WARDEN_SHIELD = 20;
export const WARDEN_REFRESH_S = 4;
/** §7.5 affix.shielded — 50% of max HP, 10%/s regen after 3 s, 300 ms break stagger. */
export const SHIELDED_POOL_PCT = 0.5;
export const SHIELDED_REGEN_PCT_S = 0.1;
export const SHIELDED_REGEN_DELAY_S = 3;
export const SHIELDED_BREAK_STAGGER_MS = 300;

function scheduleBurst(kind: number, x: number, y: number, r: number, dmg: number, tick: number, owner: number): void {
  if (burstCount >= BURST_CAP) return;
  bKind[burstCount] = kind;
  bX[burstCount] = x;
  bY[burstCount] = y;
  bR[burstCount] = r;
  bDmg[burstCount] = dmg;
  bTick[burstCount] = tick;
  bOwner[burstCount] = owner;
  burstCount++;
}

// --- injectable sinks (integrator wiring, INTERFACES.md) ---

export type PlayerDamageSink = (w: World, sourceId: number, sourceDefId: string, amount: number) => void;
export type EnemyProjectileSpawner = (
  w: World,
  ownerId: number,
  ownerDefId: string,
  x: number,
  y: number,
  vx: number,
  vy: number,
  damage: number,
  radius: number,
  lifeTicks: number,
  statusId: string,
  visualClass: number,
  behaviour: number
) => Projectile | null;

let damageSink: PlayerDamageSink | null = null;
let projSpawner: EnemyProjectileSpawner | null = null;

/** R1 installs damage.ts's canonical path here; the fallback below is self-contained. */
export function setPlayerDamageSink(fn: PlayerDamageSink | null): void {
  damageSink = fn;
}

/** R2 installs projectiles.ts's spawner here; enemy projectiles then belong to R2. */
export function setEnemyProjectileSpawner(fn: EnemyProjectileSpawner | null): void {
  projSpawner = fn;
  ownsProjectiles = fn === null;
}

/** Applies enemy-side damage to the player, honouring dash i-frames and mercy (§03). */
export function dealPlayerDamage(w: World, sourceId: number, sourceDefId: string, amount: number): void {
  if (damageSink) {
    damageSink(w, sourceId, sourceDefId, amount);
    return;
  }
  const p = w.player;
  if (!p.alive || amount <= 0) return;
  if (p.dash.active && w.tick < p.dash.iframes_until) return;
  if (w.tick < p.mercy_until) return;
  let dmg = amount - p.stats.armor;
  if (dmg < 1) dmg = 1;
  p.hp -= dmg;
  if (p.hp < 0) p.hp = 0;
  p.mercy_until = w.tick + Math.round(cfg.get('player.mercy_s') * 60);
  w.counters.damage_taken += dmg;
  w.bus.emit('player_hit', {
    source_id: sourceId,
    amount: dmg,
    hp_after: p.hp,
    telegraph_event_id: 0,
    source_def_id: sourceDefId,
  });
}

// --- difficulty (§11 tiers) ---

let diffId = '';
let diffHp = 1;
let diffDmg = 1;
let diffSpeed = 1;
let diffAffixCount = 1;

function syncDifficulty(w: World): void {
  if (w.difficultyId === diffId) return;
  diffId = w.difficultyId;
  diffHp = 1;
  diffDmg = 1;
  diffSpeed = 1;
  diffAffixCount = ELITE_MODIFIER.affix_count.default;
  const tier = difficultyById.get(diffId);
  if (!tier) return;
  const m = objOf(tier, 'stat_multipliers');
  diffHp = numOf(m, 'enemy_hp', 1);
  diffDmg = numOf(m, 'enemy_damage', 1);
  diffSpeed = numOf(m, 'enemy_speed', 1);
  const comp = objOf(tier, 'composition_changes');
  diffAffixCount = numOf(comp, 'elite_affix_count', ELITE_MODIFIER.affix_count.default);
}

// --- spawn / init ---

const scratchAffix: string[] = ['', ''];

/**
 * §7.5 affix draw: uniform over the 5 affixes, no duplicate within one elite.
 * Consumes `w.rng.spawn` (spawn-time decision, §8.12). Returns the count written.
 */
export function rollAffixes(w: World, out: string[]): number {
  syncDifficulty(w);
  let n = diffAffixCount | 0;
  if (n < 0) n = 0;
  if (n > 2) n = 2;
  if (n === 0) return 0;
  const a = w.rng.spawn.next_int(AFFIX_IDS.length);
  out[0] = AFFIX_IDS[a];
  if (n === 1) return 1;
  let b = w.rng.spawn.next_int(AFFIX_IDS.length - 1);
  if (b >= a) b++;
  out[1] = AFFIX_IDS[b];
  return 2;
}

function affixBit(id: string): number {
  switch (id) {
    case 'affix.shielded':
      return AFFIX_SHIELDED;
    case 'affix.volatile':
      return AFFIX_VOLATILE;
    case 'affix.swift':
      return AFFIX_SWIFT;
    case 'affix.warden':
      return AFFIX_WARDEN;
    case 'affix.bristling':
      return AFFIX_BRISTLING;
    default:
      return 0;
  }
}

function affixThreat(id: string): number {
  for (let i = 0; i < AFFIXES.length; i++) {
    if (AFFIXES[i].id === id) return AFFIXES[i].threat_cost;
  }
  return 0;
}

/** §7.2 conventions — HP(t) = hp_base × 1.6^(t/120). */
export function hpScale(seconds: number): number {
  return Math.pow(HP_GROWTH_BASE, seconds / GROWTH_PERIOD_S);
}

/** §7.2 conventions — dmg(t) = dmg_base × 1.08^(t/120), capped at ×2.0. */
export function damageScale(seconds: number): number {
  const s = Math.pow(DMG_GROWTH_BASE, seconds / GROWTH_PERIOD_S);
  return s > DMG_GROWTH_CAP ? DMG_GROWTH_CAP : s;
}

/**
 * Writes every def-derived field onto `e`. Elite multipliers per §7.5:
 * HP ×4, contact ×1.5, speed ×1.10, scale ×1.5, XP ×5, threat ×4 + affix costs.
 * When `elite` is true and `e.affix_count` is 0, affixes are rolled here.
 */
export function initEnemyFromDef(w: World, e: Enemy, defId: string, elite: boolean): void {
  syncDifficulty(w);
  const di = enemyDefIndex(defId);
  const d = DEFS[di < 0 ? 0 : di];
  const i = e.pool_index;
  const t = w.tick / 60;
  const m = ELITE_MODIFIER.multipliers;

  sDef[i] = d.index;
  e.def_id = d.id;
  e.kind = 'enemy';
  e.faction = 'enemy';
  e.elite = elite;
  e.is_boss = false;

  if (elite && e.affix_count === 0) {
    const n = rollAffixes(w, scratchAffix);
    for (let k = 0; k < n; k++) e.affixes[k] = scratchAffix[k];
    e.affix_count = n;
  }
  if (!elite) e.affix_count = 0;

  let mask = 0;
  let affixCost = 0;
  for (let k = 0; k < e.affix_count; k++) {
    mask |= affixBit(e.affixes[k]);
    affixCost += affixThreat(e.affixes[k]);
  }
  sAffix[i] = mask;

  const eliteHp = elite ? m.hp : 1;
  const eliteDmg = elite ? m.contact_damage : 1;
  const eliteSpd = elite ? m.move_speed : 1;
  const eliteScale = elite ? m.scale : 1;

  e.max_hp = d.hpBase * hpScale(t) * eliteHp * diffHp;
  e.hp = e.max_hp;
  e.hp_frac = 1;
  e.scale = eliteScale;
  e.hitbox_radius = d.radius * eliteScale;
  e.threat_score = elite ? d.threat * m.threat_cost + affixCost : d.threat;

  let speed = d.moveSpeed * eliteSpd * diffSpeed;
  let turn = d.turnRate;
  if (mask & AFFIX_SWIFT) {
    speed *= 1.35;
    turn *= 1.3;
  }
  sSpeed[i] = speed;
  sForce[i] = d.maxForce;
  sTurn[i] = turn;
  sContact[i] = d.contactBase * damageScale(t) * eliteDmg * diffDmg;

  sShieldPool[i] = 0;
  sShieldSrc[i] = SHIELD_NONE;
  e.shield = 0;
  if (mask & AFFIX_SHIELDED) {
    sShieldPool[i] = e.max_hp * SHIELDED_POOL_PCT;
    e.shield = sShieldPool[i];
    sShieldSrc[i] = SHIELD_AFFIX;
  }
  sPrevShield[i] = e.shield;
  sPrevHp[i] = e.hp;
  e.shield_regen_at = 0;

  e.status_count = 0;
  e.age_ticks = 0;
  e.attack_state = STATE_IDLE;
  e.attack_dir.x = 1;
  e.attack_dir.y = 0;
  e.stagger_until = 0;
  e.contact_cd_until = 0;
  e.knock.x = 0;
  e.knock.y = 0;
  e.vel.x = 0;
  e.vel.y = 0;
  e.boss_phase_index = 0;
  e.boss_enrage_tick = 0;
  e.spawn_anim = 0;
  e.death_anim = 0;
  e.spawn_tick = w.tick;
  e.steering.wander = 0;
  e.steering.goal.x = w.player.pos.x;
  e.steering.goal.y = w.player.pos.y;

  const spawnTelegraph = Math.round(cfg.get('director.telegraph_min_s') * 60);
  e.telegraph_until = w.tick + spawnTelegraph;
  e.targetable = false;

  sStateEnd[i] = 0;
  sBristleReady[i] = 0;
  sWardenNext[i] = w.tick;
  sChildren[i] = 0;
  sParent[i] = -1;
  sZoneOwned[i] = 0;
  sTetherNext[i] = 0;
  sSurgeNext[i] = 0;
  sChargeYaw[i] = 0;
  sDeathHandled[i] = 0;
  sMaterialized[i] = 0;
  sDriftRot[i] = 0;
  sGoalAngle[i] = 0;

  // Stagger the first action so a formation does not fire in lockstep (`ai` stream).
  const jitter = w.rng.ai.next();
  if (d.atk === ATK_CHARGE) {
    const span = d.stalkDurMaxTicks - d.stalkDurMinTicks;
    sNextAction[i] = e.telegraph_until + d.stalkDurMinTicks + Math.floor(jitter * (span > 0 ? span : 1));
  } else if (d.cadenceTicks > 0) {
    sNextAction[i] = e.telegraph_until + Math.floor(jitter * d.cadenceTicks);
  } else if (d.atk === ATK_ZONE) {
    sNextAction[i] = e.telegraph_until + Math.floor(jitter * 30);
  } else if (d.atk === ATK_BIRTH) {
    sNextAction[i] = e.telegraph_until + d.birthCadenceTicks;
  } else if (d.atk === ATK_SUPPORT) {
    sNextAction[i] = e.telegraph_until + Math.floor(jitter * d.supportCadenceTicks);
  } else if (d.atk === ATK_BLOOM_BEAT) {
    sNextAction[i] = e.telegraph_until + d.bloomBeatTicks;
  } else {
    sNextAction[i] = e.telegraph_until;
  }
  e.attack_ready_tick = sNextAction[i];
}

/**
 * Acquires a pool slot and initialises it. Returns null when the pool or the §14
 * live cap denies the spawn (deterministic denial, §14.3).
 */
export function spawnEnemy(
  w: World,
  defId: string,
  x: number,
  y: number,
  elite: boolean,
  affixes: string[]
): Enemy | null {
  if (w.enemies.count >= cfg.get('budget.max_enemies')) {
    w.bus.emit('pool_exhausted', { pool: 'enemies', denied_kind: defId });
    return null;
  }
  const e = w.enemies.acquire();
  if (!e) {
    w.bus.emit('pool_exhausted', { pool: 'enemies', denied_kind: defId });
    return null;
  }
  e.id = w.allocId();
  e.pos.x = x;
  e.pos.y = y;
  e.affix_count = 0;
  if (elite && affixes.length > 0) {
    const n = affixes.length > 2 ? 2 : affixes.length;
    for (let k = 0; k < n; k++) e.affixes[k] = affixes[k];
    e.affix_count = n;
  }
  initEnemyFromDef(w, e, defId, elite);
  w.wrapToArena(e.pos, e.hitbox_radius);
  e.steering.heading = Math.atan2(wrapDeltaY(w.player.pos.y - e.pos.y), wrapDeltaX(w.player.pos.x - e.pos.x));
  w.bus.emit('spawn', {
    entity_id: e.id,
    kind: 'enemy',
    def_id: e.def_id,
    pos: e.pos,
    elite: e.elite,
    affixes: e.affixes,
  });
  return e;
}

// --- read API for other systems ---

/** Time-scaled contact damage for this enemy (R1's collision.ts reads this). */
export function enemyContactDamage(e: Enemy): number {
  return sContact[e.pool_index];
}

/** Effective max speed after elite, difficulty and affix.swift multipliers (§7.5). */
export function enemyMaxSpeed(e: Enemy): number {
  return sSpeed[e.pool_index];
}

/** Effective turn rate in rad/s after affix.swift (§7.5). */
export function enemyTurnRate(e: Enemy): number {
  return sTurn[e.pool_index];
}

/** XP motes this archetype drops, elite ×5 (§7.5). R5 reads this. */
export function enemyXpValue(e: Enemy): number {
  const d = DEFS[sDef[e.pool_index]];
  const base = d.xpMotes;
  return e.elite ? base * ELITE_MODIFIER.multipliers.xp : base;
}

/**
 * §7.2.6 armor + §7.2.7/§7.2.12 vulnerable windows. Multiplier applied to incoming
 * damage from a source at (srcX, srcY). Basalt crab: 120° frontal arc, ×0.4.
 */
export function enemyIncomingDamageMult(e: Enemy, srcX: number, srcY: number): number {
  const i = e.pool_index;
  const d = DEFS[sDef[i]];
  let mult = 1;
  if (d.armorArcDeg > 0) {
    const dx = wrapDeltaX(srcX - e.pos.x);
    const dy = wrapDeltaY(srcY - e.pos.y);
    if (dx * dx + dy * dy > 1e-9) {
      let a = Math.atan2(dy, dx) - e.steering.heading;
      if (a > Math.PI) a -= TAU;
      else if (a < -Math.PI) a += TAU;
      if (Math.abs(a) <= d.armorArcDeg * 0.5 * DEG) mult *= 1 - d.armorReduction;
    }
  }
  if (e.attack_state === STATE_RECOVERING && d.recoveryDamageMult > 1) mult *= d.recoveryDamageMult;
  return mult;
}

/** True while the enemy is still materialising and must not be targeted (§8.11). */
export function enemyIsMaterialising(w: World, e: Enemy): boolean {
  return w.tick < e.telegraph_until && sMaterialized[e.pool_index] === 0;
}

export function enemyAffixMask(e: Enemy): number {
  return sAffix[e.pool_index];
}

export function activeNettleZones(): number {
  return liveZones;
}

/** Clears all per-slot state. Call from the integrator on world reset. */
export function installEnemies(w: World): void {
  sDef.fill(0);
  sAffix.fill(0);
  sSpeed.fill(0);
  sForce.fill(0);
  sTurn.fill(0);
  sContact.fill(0);
  sShieldPool.fill(0);
  sShieldSrc.fill(0);
  sPrevShield.fill(0);
  sPrevHp.fill(0);
  sStateEnd.fill(0);
  sNextAction.fill(0);
  sBristleReady.fill(0);
  sWardenNext.fill(0);
  sChildren.fill(0);
  sParent.fill(-1);
  sZoneOwned.fill(0);
  sTetherNext.fill(0);
  sSurgeNext.fill(0);
  sChargeYaw.fill(0);
  sGoalAngle.fill(0);
  sDeathHandled.fill(0);
  sMaterialized.fill(0);
  sDriftRot.fill(0);
  ownedProj.fill(0);
  burstCount = 0;
  liveZones = 0;
  diffId = '';
  resetFleeWindow(w);
}

// --- step 4 ---

const nearCand = new Int32Array(NEIGHBOUR_CAP);

/** Sim step 4: steering, formations, attack state machines, elite/affix upkeep. */
export function stepEnemies(w: World): void {
  syncDifficulty(w);
  stepFleeWindow(w);
  beginSteerFrame(w);
  processBursts(w);
  if (ownsProjectiles) stepEnemyProjectiles(w);

  const list = w.enemies.liveIndices();
  for (let li = 0; li < list.length; li++) {
    const idx = list[li];
    const e = w.enemies.slots[idx];
    if (!e.alive || e.is_boss) continue;

    if (e.death_anim > 0) {
      advanceDeath(e);
      continue;
    }
    if (e.hp <= 0) {
      onEnemyDeath(w, e);
      continue;
    }

    // Spawn telegraph gate (§8.11): not targetable, no movement, no damage.
    if (sMaterialized[idx] === 0) {
      const span = e.telegraph_until - e.spawn_tick;
      const prog = span > 0 ? (w.tick - e.spawn_tick) / span : 1;
      e.spawn_anim = prog < 0 ? 0 : prog > 1 ? 1 : prog;
      e.targetable = false;
      if (w.tick >= e.telegraph_until) {
        e.spawn_anim = 1;
        e.targetable = true;
        sMaterialized[idx] = 1;
        w.bus.emit('materialize', { entity_id: e.id });
      } else {
        continue;
      }
    }

    e.age_ticks++;
    e.hp_frac = e.max_hp > 0 ? e.hp / e.max_hp : 0;

    upkeepShield(w, e);
    upkeepBristling(w, e);
    if (sAffix[idx] & AFFIX_WARDEN) upkeepWarden(w, e);

    if (antiStall(w, e)) continue;

    const d = DEFS[sDef[idx]];
    const scriptedMove = runAttack(w, e, d);
    if (!scriptedMove) {
      if (d.stationary) {
        e.vel.x = 0;
        e.vel.y = 0;
      } else if (w.tick < e.stagger_until) {
        e.vel.x *= 0.85;
        e.vel.y *= 0.85;
      } else {
        updateFormationGoal(w, e, d);
        const ramp = stallRamp(e);
        const hunt = huntFactor(w, e, d);
        let maxSpeed = sSpeed[idx] * ramp;
        if (hunt > 0 && fleeFactor > 0) {
          // Raise toward the ceiling, never downward toward it: a dart-eel at 10.5 u/s
          // and a swift elite keep every multiplier §7.5 gave them.
          //
          // AMD-46 — the SPEED channel is the straightness term alone, never the anchor.
          // §8.10 exists to close a kinematic deficit, and a planted ship opens none: every
          // archetype already arrives. Ramping to 1.25× the player's top speed against a
          // stationary target buys nothing but lethality, and measured it was a coin flip —
          // three seeds ran 10.59 / 10.66 / 2.19 with the anchor on the speed channel and
          // 10.6 / 10.5 / 9.6 with it on commitment only. The anchor gets the steering
          // channel below, which is what "the swarm commits to you" actually means.
          const ceiling = playerTopSpeed(w) * HUNT_OVERTAKE;
          if (ceiling > maxSpeed) maxSpeed += (ceiling - maxSpeed) * fleeFactor;
        } else if (huntAttached) {
          // Arrived, and the player is still running: hold station instead of sliding out.
          // Keyed on the straightness term alone — a body that has arrived on a *planted*
          // ship has nothing to keep station with, and 8 u/s against a 2 u formation offset
          // is an overshoot, not a grind.
          const hold = playerTopSpeed(w) * HUNT_HOLD * fleeFactor;
          if (hold > maxSpeed) maxSpeed = hold;
        }
        steerEnemy(
          w,
          e,
          d.wSep,
          d.wAli,
          d.wCoh,
          d.wPur,
          d.wAvo,
          d.wForm,
          maxSpeed,
          sForce[idx],
          sTurn[idx],
          d.pursuitLead,
          e.steering.goal.x,
          e.steering.goal.y,
          hunt,
          STEP
        );
      }
    }

    integrate(w, e);
  }

  resolveSeparation(w);
}

/** §8.10 aggression ramp: +1.5% max speed per second alive past 45 s, capped +40%. */
export function stallRamp(e: Enemy): number {
  const age = e.age_ticks / 60;
  if (age <= STALL_RAMP_START_S) return 1;
  let r = (age - STALL_RAMP_START_S) * STALL_RAMP_PER_S;
  if (r > STALL_RAMP_CAP) r = STALL_RAMP_CAP;
  return 1 + r;
}

/**
 * §8.10 hunt pressure for one enemy, in [0,1]. The global {@link huntPressure} says the
 * player is denying the swarm a fight — by running, or by planting; this says whether *this*
 * body is one of the ones not currently in it.
 * Distance is the short way round the torus — a raw `e.pos.x − player.pos.x` reads a body
 * 1 u across the seam as 199 u away and would hunt it forever (AMD-24).
 *
 * An enemy already inside its own engagement range gets no ramp: the rule has nothing to
 * correct. It does get the {@link HUNT_HOLD} station-keeping floor, applied by the caller,
 * so arriving is not immediately undone.
 *
 * §8.12 holds: nothing here reads HP, DPS, deaths or any other performance signal.
 * O(1), no query, no allocation, no RNG.
 */
function huntFactor(w: World, e: Enemy, d: DefRuntime): number {
  if (!huntEnabled || d.huntReach <= 0 || huntPressure <= 0) {
    huntAttached = false;
    return 0;
  }
  const dx = wrapDeltaX(e.pos.x - w.player.pos.x);
  const dy = wrapDeltaY(e.pos.y - w.player.pos.y);
  if (dx * dx + dy * dy <= d.huntReach * d.huntReach) {
    huntAttached = true;
    return 0;
  }
  huntAttached = false;
  return huntPressure;
}

/**
 * Set by `huntFactor` and read by its single caller on the next line. A field rather than
 * a second return value because the step loop allocates nothing (§14.3) and a second
 * distance test would be a second square root per enemy per tick.
 */
let huntAttached = false;

// --- §8.10 flee detection: the straightness of the player's recent path ---

const FLEE_LEN = FLEE_WINDOW_S * 60 + 1;
/** Offset from the post-increment head back to the sample `ANCHOR_WINDOW_S` seconds ago. */
const ANCHOR_OFFSET = FLEE_LEN - 1 - ANCHOR_WINDOW_S * 60;
/** Ring buffer of the player's *unwrapped* cumulative position. Allocated once. */
const fleeX = new Float64Array(FLEE_LEN);
const fleeY = new Float64Array(FLEE_LEN);
/** Ring buffer of cumulative path LENGTH — displacement's odometer twin (AMD-46). */
const fleeP = new Float64Array(FLEE_LEN);
let fleeHead = 0;
let fleeFilled = 0;
let fleeAccX = 0;
let fleeAccY = 0;
let fleeAccP = 0;
let fleePrevX = 0;
let fleePrevY = 0;
let fleeFactor = 0;
let anchorFactor = 0;
let huntPressure = 0;

/** Clears the flee window. Called from `installEnemies`, alongside every other run reset. */
export function resetFleeWindow(w: World): void {
  fleeHead = 0;
  fleeFilled = 0;
  fleeAccX = 0;
  fleeAccY = 0;
  fleeAccP = 0;
  fleePrevX = w.player.pos.x;
  fleePrevY = w.player.pos.y;
  fleeFactor = 0;
  anchorFactor = 0;
  huntPressure = 0;
  fleeX.fill(0);
  fleeY.fill(0);
  fleeP.fill(0);
}

/**
 * One tick of the flee window. The cumulative position integrates *wrapped* per-tick
 * deltas, so a lap of the torus reads as 200 u of travel rather than as returning to the
 * start — which is the whole point on a surface with no edge, where a straight line comes
 * back to where it began. Straightness is then net displacement over the window divided by
 * the displacement a player at top speed would have covered.
 *
 * The same per-tick delta feeds a second odometer that sums LENGTH rather than displacement.
 * Displacement over length is what separates the three protocols: a sprint scores 1 on both,
 * an orbit scores ~0.5 on displacement and ~1 on length, and an anchor scores 0 on both. The
 * flee term reads the first ratio over 20 s; the anchor term reads the second over 5 s.
 *
 * Both are gated on the full 20 s buffer, so a run never opens with either armed however the
 * player spends the first seconds — including standing on the spawn point.
 *
 * Cost: three adds, two subtracts, two square roots per tick, total, for the whole sim.
 */
function stepFleeWindow(w: World): void {
  const p = w.player;
  const sx = wrapDeltaX(p.pos.x - fleePrevX);
  const sy = wrapDeltaY(p.pos.y - fleePrevY);
  fleeAccX += sx;
  fleeAccY += sy;
  fleeAccP += Math.sqrt(sx * sx + sy * sy);
  fleePrevX = p.pos.x;
  fleePrevY = p.pos.y;
  fleeX[fleeHead] = fleeAccX;
  fleeY[fleeHead] = fleeAccY;
  fleeP[fleeHead] = fleeAccP;
  fleeHead = fleeHead + 1 === FLEE_LEN ? 0 : fleeHead + 1;
  if (fleeFilled < FLEE_LEN) fleeFilled++;
  if (fleeFilled < FLEE_LEN) {
    fleeFactor = 0;
    anchorFactor = 0;
    huntPressure = 0;
    return;
  }
  const top = playerTopSpeed(w);
  const dx = fleeAccX - fleeX[fleeHead];
  const dy = fleeAccY - fleeY[fleeHead];
  const reach = top * FLEE_WINDOW_S;
  const straight = reach > 1e-6 ? Math.sqrt(dx * dx + dy * dy) / reach : 0;
  if (straight <= FLEE_LOW) fleeFactor = 0;
  else if (straight >= FLEE_HIGH) fleeFactor = 1;
  else fleeFactor = (straight - FLEE_LOW) / (FLEE_HIGH - FLEE_LOW);

  let ai = fleeHead + ANCHOR_OFFSET;
  if (ai >= FLEE_LEN) ai -= FLEE_LEN;
  const roam = top * ANCHOR_WINDOW_S;
  const travel = roam > 1e-6 ? (fleeAccP - fleeP[ai]) / roam : 1;
  if (travel <= ANCHOR_STILL) anchorFactor = 1;
  else if (travel >= ANCHOR_MOVE) anchorFactor = 0;
  else anchorFactor = (ANCHOR_MOVE - travel) / (ANCHOR_MOVE - ANCHOR_STILL);

  if (!anchorEnabled) anchorFactor = 0;
  huntPressure = fleeFactor > anchorFactor ? fleeFactor : anchorFactor;
}

/** Diagnostic read of the §8.10 straightness term alone. Never read by sim logic. */
export function currentFleeFactor(): number {
  return fleeFactor;
}

/** Diagnostic read of the AMD-46 anchor term alone. Never read by sim logic. */
export function currentAnchorFactor(): number {
  return anchorFactor;
}

/** Diagnostic read of the combined §8.10 hunt pressure — `max(flee, anchor)`. */
export function currentHuntPressure(): number {
  return huntPressure;
}

/**
 * The kinematic ceiling a full hunt closes toward. Read from the player's own top speed so
 * a move-speed build cannot re-open the exploit; §4.2's +40% cap is mirrored here rather
 * than imported, so this stays independent of `player.ts`. Speed is geometry, not
 * performance — §8.12 forbids reading HP, DPS and death count, and this reads none of them.
 */
function playerTopSpeed(w: World): number {
  const mult = w.player.stats.move_speed_mult;
  const capped = mult > HUNT_MOVE_MULT_CAP ? HUNT_MOVE_MULT_CAP : mult;
  return cfg.get('player.move_speed') * capped;
}

/**
 * A/B switch for the hunt ramp, on the same footing as director.ts's
 * `setOverpopGuardSuspended`: off is only ever set by a measurement harness, and the
 * artifact that records a number records the flag beside it. On in every real run.
 */
let huntEnabled = true;

export function setHuntRampEnabled(v: boolean): void {
  huntEnabled = v;
}

/**
 * The same A/B switch for the AMD-46 anchor term alone, so a sweep can separate "the swarm
 * commits to a planted ship" from "the swarm runs down a fleeing one". On in every real run.
 */
let anchorEnabled = true;

export function setAnchorPressureEnabled(v: boolean): void {
  anchorEnabled = v;
}

export function isAnchorPressureEnabled(): boolean {
  return anchorEnabled;
}

export function isHuntRampEnabled(): boolean {
  return huntEnabled;
}

/** §8.10 stalemate break: 90 s alive self-terminates, in camera view only (G4). */
function antiStall(w: World, e: Enemy): boolean {
  if (e.age_ticks < STALL_TERMINATE_S * 60) return false;
  const dx = wrapDeltaX(e.pos.x - w.player.pos.x);
  const dy = wrapDeltaY(e.pos.y - w.player.pos.y);
  if (dx * dx + dy * dy > IN_VIEW_RADIUS_U * IN_VIEW_RADIUS_U) return false;
  e.hp = 0;
  w.bus.emit('death', {
    entity_id: e.id,
    killer_id: 0,
    cause_id: 'anti_stall',
    pos: e.pos,
    elite: e.elite,
    def_id: e.def_id,
    threat: e.threat_score,
  });
  onEnemyDeath(w, e);
  return true;
}

function integrate(w: World, e: Enemy): void {
  e.pos.x += (e.vel.x + e.knock.x) * STEP;
  e.pos.y += (e.vel.y + e.knock.y) * STEP;
  const decay = cfg.get('player.knockback_decay') * STEP;
  const kl = Math.sqrt(e.knock.x * e.knock.x + e.knock.y * e.knock.y);
  if (kl > 0) {
    const s = kl - decay;
    if (s <= 0) {
      e.knock.x = 0;
      e.knock.y = 0;
    } else {
      e.knock.x = (e.knock.x / kl) * s;
      e.knock.y = (e.knock.y / kl) * s;
    }
  }
  w.wrapToArena(e.pos, e.hitbox_radius);
}

/** Formation-goal target: range-band hold, or a slot-offset flank for pursuers (§7.3 F). */
function updateFormationGoal(w: World, e: Enemy, d: DefRuntime): void {
  const i = e.pool_index;
  const px = w.player.pos.x;
  const py = w.player.pos.y;
  if (d.bandMin > 0) {
    const dx = wrapDeltaX(e.pos.x - px);
    const dy = wrapDeltaY(e.pos.y - py);
    const dist = Math.sqrt(dx * dx + dy * dy);
    const mid = (d.bandMin + d.bandMax) * 0.5;
    let ax: number;
    let ay: number;
    if (dist > 1e-4) {
      ax = dx / dist;
      ay = dy / dist;
    } else {
      ax = 1;
      ay = 0;
    }
    e.steering.goal.x = wrapX(px + ax * mid);
    e.steering.goal.y = wrapY(py + ay * mid);
    return;
  }
  if (d.atk === ATK_CHARGE && e.attack_state === STATE_IDLE && d.stalkMin > 0) {
    const dx = wrapDeltaX(e.pos.x - px);
    const dy = wrapDeltaY(e.pos.y - py);
    const dist = Math.sqrt(dx * dx + dy * dy);
    const mid = (d.stalkMin + d.stalkMax) * 0.5;
    const ax = dist > 1e-4 ? dx / dist : 1;
    const ay = dist > 1e-4 ? dy / dist : 0;
    e.steering.goal.x = wrapX(px + ax * mid);
    e.steering.goal.y = wrapY(py + ay * mid);
    return;
  }
  // Slot-based lateral offset so a group flanks instead of stacking on one point.
  const a = sGoalAngle[i] !== 0 ? sGoalAngle[i] : (e.steering.formation_slot * 2.399963229728653) % TAU;
  sGoalAngle[i] = a;
  const off = 1.5 + (e.steering.formation_slot % 5) * 0.6;
  e.steering.goal.x = wrapX(px + Math.cos(a) * off);
  e.steering.goal.y = wrapY(py + Math.sin(a) * off);
}

// --- attack state machines ---

/** Returns true when the archetype drove its own velocity (scripted state, §7.3). */
function runAttack(w: World, e: Enemy, d: DefRuntime): boolean {
  switch (d.atk) {
    case ATK_PROJECTILE:
      rangedMachine(w, e, d, false);
      return false;
    case ATK_LOBBED:
      rangedMachine(w, e, d, true);
      return false;
    case ATK_MELEE:
      meleeMachine(w, e, d);
      return false;
    case ATK_CHARGE:
      return chargeMachine(w, e, d);
    case ATK_ZONE:
      zoneMachine(w, e, d);
      return false;
    case ATK_TETHER:
      return tetherMachine(w, e, d);
    case ATK_AMBUSH:
      return ambushMachine(w, e, d);
    case ATK_SUPPORT:
      supportMachine(w, e, d);
      return false;
    case ATK_BIRTH:
      birthMachine(w, e, d);
      return false;
    case ATK_BLOOM_BEAT:
      return curtainMachine(w, e, d);
    default:
      return false;
  }
}

function distToPlayer(w: World, e: Enemy): number {
  const dx = wrapDeltaX(w.player.pos.x - e.pos.x);
  const dy = wrapDeltaY(w.player.pos.y - e.pos.y);
  return Math.sqrt(dx * dx + dy * dy);
}

/** §7.2.4 / §7.2.11 — wind up, then release one projectile. Never fires out of band. */
function rangedMachine(w: World, e: Enemy, d: DefRuntime, lobbed: boolean): void {
  const i = e.pool_index;
  const dist = distToPlayer(w, e);
  if (e.attack_state === STATE_WINDING) {
    // Abort if the player closed inside the band: "fires zero shots while repositioning".
    if (dist < d.bandMin || dist > d.projRange) {
      e.attack_state = STATE_IDLE;
      sNextAction[i] = w.tick + 30;
      e.attack_ready_tick = sNextAction[i];
      return;
    }
    if (w.tick >= sStateEnd[i]) {
      fireBarb(w, e, d, lobbed);
      e.attack_state = STATE_IDLE;
      sNextAction[i] = w.tick + d.cadenceTicks;
      e.attack_ready_tick = sNextAction[i];
    }
    return;
  }
  if (w.tick < sNextAction[i]) return;
  if (dist < d.bandMin || dist > d.projRange) return;
  const wind = ticksOf(Math.max(d.def ? d.def.telegraph.duration_ms : 0, MIN_DAMAGING_TELEGRAPH_MS));
  e.attack_state = STATE_WINDING;
  sStateEnd[i] = w.tick + wind;
  e.telegraph_until = sStateEnd[i];
  const dx = wrapDeltaX(w.player.pos.x - e.pos.x);
  const dy = wrapDeltaY(w.player.pos.y - e.pos.y);
  const l = Math.sqrt(dx * dx + dy * dy);
  e.attack_dir.x = l > 1e-6 ? dx / l : 1;
  e.attack_dir.y = l > 1e-6 ? dy / l : 0;
}

function fireBarb(w: World, e: Enemy, d: DefRuntime, lobbed: boolean): void {
  const dx = wrapDeltaX(w.player.pos.x - e.pos.x);
  const dy = wrapDeltaY(w.player.pos.y - e.pos.y);
  const l = Math.sqrt(dx * dx + dy * dy);
  const ux = l > 1e-6 ? dx / l : e.attack_dir.x;
  const uy = l > 1e-6 ? dy / l : e.attack_dir.y;
  const speed = d.projSpeed;
  const life = speed > 0 ? Math.ceil((d.projRange / speed) * 60) : 60;
  spawnEnemyProjectile(
    w,
    e.id,
    e.def_id,
    e.pos.x + ux * (e.hitbox_radius + 0.2),
    e.pos.y + uy * (e.hitbox_radius + 0.2),
    ux * speed,
    uy * speed,
    d.projDamage * damageScale(w.tick / 60),
    lobbed ? 0.5 : 0.3,
    life,
    lobbed ? 'status.ink' : '',
    lobbed ? VISUAL_ENEMY_GLOB : VISUAL_ENEMY_BARB,
    lobbed ? BEHAVIOUR_ENEMY_LOBBED : BEHAVIOUR_ENEMY_LINEAR
  );
}

/** §7.2.6 — claw swipe at 2.5 u behind a 450 ms telegraph. */
function meleeMachine(w: World, e: Enemy, d: DefRuntime): void {
  const i = e.pool_index;
  const dist = distToPlayer(w, e);
  if (e.attack_state === STATE_WINDING) {
    if (w.tick >= sStateEnd[i]) {
      if (dist <= d.meleeRange + w.player.hitbox_radius + e.hitbox_radius) {
        dealPlayerDamage(w, e.id, e.def_id, d.meleeDamage * damageScale(w.tick / 60));
      }
      e.attack_state = STATE_IDLE;
      sNextAction[i] = w.tick + Math.round(CLAW_COOLDOWN_S * 60);
      e.attack_ready_tick = sNextAction[i];
    }
    return;
  }
  if (w.tick < sNextAction[i]) return;
  if (dist > d.meleeRange + w.player.hitbox_radius + e.hitbox_radius) return;
  const wind = ticksOf(Math.max(d.def ? d.def.telegraph.duration_ms : 0, MIN_DAMAGING_TELEGRAPH_MS));
  e.attack_state = STATE_WINDING;
  sStateEnd[i] = w.tick + wind;
  e.telegraph_until = sStateEnd[i];
}

/** §7.2.7 — stalk → 600 ms aim → committed charge (≤15° total correction) → recovery. */
function chargeMachine(w: World, e: Enemy, d: DefRuntime): boolean {
  const i = e.pool_index;
  if (e.attack_state === STATE_WINDING) {
    e.vel.x *= 0.8;
    e.vel.y *= 0.8;
    const dx = wrapDeltaX(w.player.pos.x - e.pos.x);
    const dy = wrapDeltaY(w.player.pos.y - e.pos.y);
    const l = Math.sqrt(dx * dx + dy * dy);
    if (l > 1e-6) {
      e.attack_dir.x = dx / l;
      e.attack_dir.y = dy / l;
    }
    e.steering.heading = Math.atan2(e.attack_dir.y, e.attack_dir.x);
    if (w.tick >= sStateEnd[i]) {
      e.attack_state = STATE_COMMITTED;
      sStateEnd[i] = w.tick + d.chargeTicks;
      sChargeYaw[i] = 0;
      e.vel.x = e.attack_dir.x * d.chargeSpeed;
      e.vel.y = e.attack_dir.y * d.chargeSpeed;
      e.steering.heading = Math.atan2(e.vel.y, e.vel.x);
    }
    return true;
  }
  if (e.attack_state === STATE_COMMITTED) {
    // Committed: turn clamped to charge_state.turn_rate and to a 15° total budget.
    const dx = wrapDeltaX(w.player.pos.x - e.pos.x);
    const dy = wrapDeltaY(w.player.pos.y - e.pos.y);
    const want = Math.atan2(dy, dx);
    let delta = want - e.steering.heading;
    if (delta > Math.PI) delta -= TAU;
    else if (delta < -Math.PI) delta += TAU;
    const step = d.chargeTurn * STEP;
    if (delta > step) delta = step;
    else if (delta < -step) delta = -step;
    const budget = d.chargeCorrectionRad - Math.abs(sChargeYaw[i]);
    if (budget <= 0) delta = 0;
    else if (Math.abs(delta) > budget) delta = delta > 0 ? budget : -budget;
    sChargeYaw[i] += delta;
    const h = e.steering.heading + delta;
    e.steering.heading = h;
    e.vel.x = Math.cos(h) * d.chargeSpeed;
    e.vel.y = Math.sin(h) * d.chargeSpeed;
    if (w.tick >= e.contact_cd_until && dx * dx + dy * dy <= (e.hitbox_radius + w.player.hitbox_radius + 0.5) ** 2) {
      dealPlayerDamage(w, e.id, e.def_id, d.chargeDamage * damageScale(w.tick / 60));
      e.contact_cd_until = w.tick + Math.round(BITE_COOLDOWN_S * 60);
    }
    if (w.tick >= sStateEnd[i]) {
      e.attack_state = STATE_RECOVERING;
      sStateEnd[i] = w.tick + d.recoveryTicks;
    }
    return true;
  }
  if (e.attack_state === STATE_RECOVERING) {
    e.vel.x *= 0.9;
    e.vel.y *= 0.9;
    if (w.tick >= sStateEnd[i]) {
      e.attack_state = STATE_IDLE;
      const span = d.stalkDurMaxTicks - d.stalkDurMinTicks;
      sNextAction[i] = w.tick + d.stalkDurMinTicks + (span > 0 ? w.rng.ai.next_int(span) : 0);
      e.attack_ready_tick = sNextAction[i];
    }
    return true;
  }
  const dist = distToPlayer(w, e);
  if (w.tick >= sNextAction[i] && dist <= d.stalkMax && dist >= 2) {
    const wind = ticksOf(Math.max(d.def ? d.def.telegraph.duration_ms : 0, MIN_DAMAGING_TELEGRAPH_MS));
    e.attack_state = STATE_WINDING;
    sStateEnd[i] = w.tick + wind;
    e.telegraph_until = sStateEnd[i];
    return true;
  }
  return false;
}

/** §7.2.8 — seeds a stinging zone with a 900 ms telegraph; global cap 6 (§8.4). */
function zoneMachine(w: World, e: Enemy, d: DefRuntime): void {
  const i = e.pool_index;
  if (w.tick < sNextAction[i]) return;
  if (liveZones >= d.zoneCap) {
    sNextAction[i] = w.tick + 30;
    return;
  }
  const h = w.hazards.acquire();
  if (!h) {
    sNextAction[i] = w.tick + 30;
    return;
  }
  const a = w.rng.ai.next() * TAU;
  const r = 2 + w.rng.ai.next() * 6;
  let zx = wrapX(e.pos.x + Math.cos(a) * r);
  let zy = wrapY(e.pos.y + Math.sin(a) * r);
  // Damaging zones must be dodgeable: keep the fill off the player's current spot.
  const pdx = wrapDeltaX(zx - w.player.pos.x);
  const pdy = wrapDeltaY(zy - w.player.pos.y);
  const pd = Math.sqrt(pdx * pdx + pdy * pdy);
  const minGap = d.zoneRadius + w.player.hitbox_radius + 1;
  if (pd < minGap) {
    const s = pd > 1e-4 ? minGap / pd : minGap;
    zx = wrapX(w.player.pos.x + (pd > 1e-4 ? pdx * s : minGap));
    zy = wrapY(w.player.pos.y + (pd > 1e-4 ? pdy * s : 0));
  }
  h.id = w.allocId();
  h.kind = 'hazard';
  h.faction = 'enemy';
  h.pos.x = zx;
  h.pos.y = zy;
  h.vel.x = 0;
  h.vel.y = 0;
  h.hazard_id = HAZARD_NETTLE_ZONE;
  h.zone.shape = 'circle';
  h.zone.r = d.zoneRadius;
  h.zone.w = 0;
  h.zone.h = 0;
  h.rot = 0;
  h.hp = 1;
  h.max_hp = 1;
  h.hitbox_radius = d.zoneRadius;
  h.targetable = false;
  h.status_count = 0;
  h.spawn_tick = w.tick;
  h.damage_per_s = d.zoneDps;
  h.status_id = '';
  h.owner_id = e.id;
  const tele = ticksOf(Math.max(d.def ? d.def.telegraph.duration_ms : 0, MIN_DAMAGING_TELEGRAPH_MS));
  h.telegraph_until = w.tick + tele;
  h.arm_tick = w.tick + tele;
  h.end_tick = h.arm_tick + d.zoneActiveTicks;
  liveZones++;
  sZoneOwned[i]++;
  e.attack_state = STATE_WINDING;
  sStateEnd[i] = h.arm_tick;
  e.telegraph_until = h.arm_tick;
  sNextAction[i] = w.tick + d.zoneCadenceTicks;
  e.attack_ready_tick = sNextAction[i];
  w.bus.emit('hazard', { hazard_id: HAZARD_NETTLE_ZONE, kind: 'telegraph', pos: h.pos, radius: d.zoneRadius });
}

/** §7.2.10 — 350 ms tether telegraph, 3 s attach, 1 surge tier / 2 s, breaks on dash. */
function tetherMachine(w: World, e: Enemy, d: DefRuntime): boolean {
  const i = e.pool_index;
  const dist = distToPlayer(w, e);
  if (e.attack_state === STATE_WINDING) {
    if (dist > d.tetherRange * 1.5) {
      e.attack_state = STATE_IDLE;
      return false;
    }
    if (w.tick >= sStateEnd[i]) {
      e.attack_state = STATE_COMMITTED;
      sStateEnd[i] = w.tick + d.tetherTicks;
      sSurgeNext[i] = w.tick + Math.round(SURGE_DRAIN_PERIOD_S * 60);
    }
    return false;
  }
  if (e.attack_state === STATE_COMMITTED) {
    const p = w.player;
    const broken = dist > TETHER_BREAK_RANGE_U || (p.dash.active && w.tick < p.dash.iframes_until);
    if (broken || w.tick >= sStateEnd[i]) {
      e.attack_state = STATE_IDLE;
      sTetherNext[i] = w.tick + Math.round(TETHER_BREAK_COOLDOWN_S * 60);
      e.attack_ready_tick = sTetherNext[i];
      return false;
    }
    dealPlayerDamage(w, e.id, e.def_id, d.tetherDps * STEP);
    if (w.tick >= sSurgeNext[i]) {
      sSurgeNext[i] = w.tick + Math.round(SURGE_DRAIN_PERIOD_S * 60);
      if (p.lumensurge_tier > 1) {
        const from = p.lumensurge_tier;
        p.lumensurge_tier = from - 1;
        w.bus.emit('surge_change', { from, to: p.lumensurge_tier, cause: 'siphon_drain' });
      }
    }
    return false;
  }
  if (w.tick < sTetherNext[i]) return false;
  if (dist > d.tetherRange) return false;
  const wind = ticksOf(Math.max(d.def ? d.def.telegraph.duration_ms : 0, MIN_DAMAGING_TELEGRAPH_MS));
  e.attack_state = STATE_WINDING;
  sStateEnd[i] = w.tick + wind;
  e.telegraph_until = sStateEnd[i];
  return false;
}

/** §7.2.12 — dormant → 450 ms wake → committed lunge at the wake-time bearing → re-bury. */
function ambushMachine(w: World, e: Enemy, d: DefRuntime): boolean {
  const i = e.pool_index;
  const dx = wrapDeltaX(w.player.pos.x - e.pos.x);
  const dy = wrapDeltaY(w.player.pos.y - e.pos.y);
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (e.attack_state === STATE_WINDING) {
    e.vel.x = 0;
    e.vel.y = 0;
    if (w.tick >= sStateEnd[i]) {
      e.attack_state = STATE_COMMITTED;
      sStateEnd[i] = w.tick + Math.round(LUREMAW_LUNGE_S * 60);
      e.vel.x = e.attack_dir.x * d.lungeSpeed;
      e.vel.y = e.attack_dir.y * d.lungeSpeed;
      e.steering.heading = Math.atan2(e.vel.y, e.vel.x);
    }
    return true;
  }
  if (e.attack_state === STATE_COMMITTED) {
    const want = Math.atan2(dy, dx);
    let delta = want - e.steering.heading;
    if (delta > Math.PI) delta -= TAU;
    else if (delta < -Math.PI) delta += TAU;
    const step = d.lungeTurn * STEP;
    if (delta > step) delta = step;
    else if (delta < -step) delta = -step;
    const h = e.steering.heading + delta;
    e.steering.heading = h;
    e.vel.x = Math.cos(h) * d.lungeSpeed;
    e.vel.y = Math.sin(h) * d.lungeSpeed;
    if (w.tick >= e.contact_cd_until && dist <= d.biteArc + e.hitbox_radius + w.player.hitbox_radius) {
      dealPlayerDamage(w, e.id, e.def_id, d.biteDamage * damageScale(w.tick / 60));
      e.contact_cd_until = w.tick + Math.round(BITE_COOLDOWN_S * 60);
      e.attack_state = STATE_RECOVERING;
      sStateEnd[i] = w.tick + d.reburyTicks;
    } else if (w.tick >= sStateEnd[i]) {
      e.attack_state = STATE_RECOVERING;
      sStateEnd[i] = w.tick + d.reburyTicks;
    }
    return true;
  }
  if (e.attack_state === STATE_RECOVERING) {
    e.vel.x *= 0.85;
    e.vel.y *= 0.85;
    if (w.tick >= sStateEnd[i]) e.attack_state = STATE_IDLE;
    return true;
  }
  e.vel.x = 0;
  e.vel.y = 0;
  if (dist <= d.ambushTrigger) {
    const wind = ticksOf(Math.max(d.def ? d.def.telegraph.duration_ms : 0, MIN_DAMAGING_TELEGRAPH_MS));
    e.attack_state = STATE_WINDING;
    sStateEnd[i] = w.tick + wind;
    e.telegraph_until = sStateEnd[i];
    const l = dist > 1e-6 ? dist : 1;
    e.attack_dir.x = dx / l;
    e.attack_dir.y = dy / l;
  }
  return true;
}

/** §7.2.9 — 500 ms cast, then bubbles on up to 3 nearest allies; never stacks. */
function supportMachine(w: World, e: Enemy, d: DefRuntime): void {
  const i = e.pool_index;
  if (e.attack_state === STATE_WINDING) {
    if (w.tick < sStateEnd[i]) return;
    e.attack_state = STATE_IDLE;
    let applied = 0;
    const n = w.hash.queryCircle(e.pos.x, e.pos.y, d.supportRange, nearCand, NEIGHBOUR_CAP);
    const stamp = w.nextStamp();
    w.seenStamp[i] = stamp;
    for (let k = 0; k < n && applied < d.supportMax; k++) {
      const oi = nearCand[k];
      if (w.seenStamp[oi] === stamp) continue;
      w.seenStamp[oi] = stamp;
      const o = w.enemies.slots[oi];
      if (!o.alive || o.death_anim > 0 || !o.targetable) continue;
      if (sShieldSrc[oi] !== SHIELD_NONE && o.shield > 0) continue;
      o.shield = d.supportPool;
      sShieldPool[oi] = d.supportPool;
      sShieldSrc[oi] = SHIELD_AEGIS;
      sPrevShield[oi] = o.shield;
      o.shield_regen_at = w.tick;
      applied++;
    }
    sNextAction[i] = w.tick + d.supportCadenceTicks;
    e.attack_ready_tick = sNextAction[i];
    return;
  }
  if (w.tick < sNextAction[i]) return;
  e.attack_state = STATE_WINDING;
  sStateEnd[i] = w.tick + ticksOf(d.def ? d.def.telegraph.duration_ms : 500);
  e.telegraph_until = sStateEnd[i];
}

/** §7.2.14 — 500 ms pod telegraph, then 3 fry; never more than 12 live children. */
function birthMachine(w: World, e: Enemy, d: DefRuntime): void {
  const i = e.pool_index;
  if (e.attack_state === STATE_WINDING) {
    if (w.tick < sStateEnd[i]) return;
    e.attack_state = STATE_IDLE;
    for (let k = 0; k < d.birthCount; k++) {
      if (sChildren[i] >= d.birthMaxChildren) break;
      const a = (k / d.birthCount) * TAU + w.rng.ai.next() * 0.4;
      const r = e.hitbox_radius + 1.2;
      const child = spawnEnemy(w, d.birthId, e.pos.x + Math.cos(a) * r, e.pos.y + Math.sin(a) * r, false, EMPTY_AFFIXES);
      if (!child) break;
      sParent[child.pool_index] = i;
      child.threat_score = 0;
      sChildren[i]++;
    }
    sNextAction[i] = w.tick + d.birthCadenceTicks;
    e.attack_ready_tick = sNextAction[i];
    return;
  }
  if (w.tick < sNextAction[i]) return;
  if (sChildren[i] >= d.birthMaxChildren) {
    sNextAction[i] = w.tick + 30;
    return;
  }
  e.attack_state = STATE_WINDING;
  sStateEnd[i] = w.tick + ticksOf(d.def ? d.def.telegraph.duration_ms : 500);
  e.telegraph_until = sStateEnd[i];
}

/** §7.2.13 — drifts a seeded rail with 6°/s rotation; bloom beat every 4 s. */
function curtainMachine(w: World, e: Enemy, d: DefRuntime): boolean {
  const i = e.pool_index;
  sDriftRot[i] += CURTAIN_DRIFT_ROT_DEG_S * DEG * STEP;
  const h = e.steering.heading + CURTAIN_DRIFT_ROT_DEG_S * DEG * STEP;
  e.steering.heading = h > Math.PI ? h - TAU : h;
  const sp = sSpeed[i];
  e.vel.x = Math.cos(e.steering.heading) * sp;
  e.vel.y = Math.sin(e.steering.heading) * sp;

  if (e.attack_state === STATE_WINDING) {
    if (w.tick < sStateEnd[i]) return true;
    e.attack_state = STATE_IDLE;
    const dist = distToPlayer(w, e);
    if (dist <= e.hitbox_radius + d.bloomAura + w.player.hitbox_radius) {
      dealPlayerDamage(w, e.id, e.def_id, sContact[i]);
    }
    sNextAction[i] = w.tick + d.bloomBeatTicks;
    e.attack_ready_tick = sNextAction[i];
    return true;
  }
  if (w.tick >= sNextAction[i]) {
    e.attack_state = STATE_WINDING;
    sStateEnd[i] = w.tick + ticksOf(Math.max(d.def ? d.def.telegraph.duration_ms : 0, MIN_DAMAGING_TELEGRAPH_MS));
    e.telegraph_until = sStateEnd[i];
  }
  return true;
}

// --- elite / affix upkeep ---

function upkeepShield(w: World, e: Enemy): void {
  const i = e.pool_index;
  if (sShieldSrc[i] === SHIELD_NONE) {
    sPrevShield[i] = e.shield;
    return;
  }
  if (e.shield < sPrevShield[i]) {
    e.shield_regen_at = w.tick + Math.round(SHIELDED_REGEN_DELAY_S * 60);
    if (e.shield <= 0) {
      e.shield = 0;
      e.stagger_until = w.tick + ticksOf(SHIELDED_BREAK_STAGGER_MS);
      if (sShieldSrc[i] !== SHIELD_AFFIX) sShieldSrc[i] = SHIELD_NONE;
    }
  } else if (e.shield < sShieldPool[i] && w.tick >= e.shield_regen_at) {
    const rate =
      sShieldSrc[i] === SHIELD_AEGIS
        ? DEFS[enemyDefIndex('enemy.aegis-medusa')].supportRegen
        : sShieldPool[i] * SHIELDED_REGEN_PCT_S;
    e.shield += rate * STEP;
    if (e.shield > sShieldPool[i]) e.shield = sShieldPool[i];
  }
  sPrevShield[i] = e.shield;
}

/** §7.5 affix.bristling — the one enemy-side damage proc, rolled on `ai` (A.1). */
function upkeepBristling(w: World, e: Enemy): void {
  const i = e.pool_index;
  if ((sAffix[i] & AFFIX_BRISTLING) === 0) {
    sPrevHp[i] = e.hp;
    return;
  }
  if (e.hp < sPrevHp[i] && w.tick >= sBristleReady[i]) {
    sBristleReady[i] = w.tick + Math.round(BRISTLING_INTERVAL_S * 60);
    if (w.rng.ai.next() < BRISTLING_CHANCE) {
      scheduleBurst(BURST_DARTS, e.pos.x, e.pos.y, 0, BRISTLING_DART_DAMAGE, w.tick + ticksOf(BRISTLING_TELEGRAPH_MS), e.id);
      e.telegraph_until = w.tick + ticksOf(BRISTLING_TELEGRAPH_MS);
    }
  }
  sPrevHp[i] = e.hp;
}

/** §7.5 affix.warden — 20 HP ally shield inside 5 u, refreshed every 4 s. */
function upkeepWarden(w: World, e: Enemy): void {
  const i = e.pool_index;
  if (w.tick < sWardenNext[i]) return;
  sWardenNext[i] = w.tick + Math.round(WARDEN_REFRESH_S * 60);
  const n = w.hash.queryCircle(e.pos.x, e.pos.y, WARDEN_RADIUS_U, nearCand, NEIGHBOUR_CAP);
  const stamp = w.nextStamp();
  w.seenStamp[i] = stamp;
  for (let k = 0; k < n; k++) {
    const oi = nearCand[k];
    if (w.seenStamp[oi] === stamp) continue;
    w.seenStamp[oi] = stamp;
    const o = w.enemies.slots[oi];
    if (!o.alive || o.death_anim > 0 || !o.targetable) continue;
    // No stacking with aegis bubbles or the shielded affix (§7.5).
    if (sShieldSrc[oi] === SHIELD_AEGIS || sShieldSrc[oi] === SHIELD_AFFIX) continue;
    if (o.shield >= WARDEN_SHIELD) continue;
    o.shield = WARDEN_SHIELD;
    sShieldPool[oi] = WARDEN_SHIELD;
    sShieldSrc[oi] = SHIELD_WARDEN;
    sPrevShield[oi] = o.shield;
    o.shield_regen_at = w.tick;
  }
}

// --- death ---

const EMPTY_AFFIXES: string[] = [];

function advanceDeath(e: Enemy): void {
  e.targetable = false;
  e.vel.x *= 0.9;
  e.vel.y *= 0.9;
  e.death_anim += 1 / DEATH_ANIM_TICKS;
  if (e.death_anim > 1) e.death_anim = 1;
}

/** Runs death consequences once: split, volatile ring, zone cleanup, child bookkeeping. */
function onEnemyDeath(w: World, e: Enemy): void {
  const i = e.pool_index;
  if (sDeathHandled[i] === 1) {
    advanceDeath(e);
    return;
  }
  sDeathHandled[i] = 1;
  e.targetable = false;
  e.hp_frac = 0;
  const d = DEFS[sDef[i]];

  if (sAffix[i] & AFFIX_VOLATILE) {
    const fire = w.tick + ticksOf(VOLATILE_TELEGRAPH_MS);
    scheduleBurst(BURST_RING, e.pos.x, e.pos.y, VOLATILE_RADIUS_U, VOLATILE_DAMAGE, fire, e.id);
    spawnVolatileTelegraph(w, e.pos.x, e.pos.y, fire, e.id);
  }
  if (d.splitCount > 0) splitInto(w, e, d);
  if (sZoneOwned[i] > 0) expireOwnedZones(w, e.id);
  const p = sParent[i];
  if (p >= 0) {
    if (sChildren[p] > 0) sChildren[p]--;
    sParent[i] = -1;
  }
  if (e.death_anim <= 0) e.death_anim = 1 / DEATH_ANIM_TICKS;
}

/** §7.2.5 — exactly 3 bloomlings fanning at 120°, time-scaled HP, 300 ms safety. */
function splitInto(w: World, e: Enemy, d: DefRuntime): void {
  const base = Math.atan2(e.vel.y, e.vel.x);
  const stepA = d.splitFanRad;
  for (let k = 0; k < d.splitCount; k++) {
    const a = base + (k - (d.splitCount - 1) * 0.5) * stepA;
    const r = e.hitbox_radius + 0.4;
    const c = spawnEnemy(w, BLOOMLING_ID, e.pos.x + Math.cos(a) * r, e.pos.y + Math.sin(a) * r, false, EMPTY_AFFIXES);
    if (!c) break;
    c.threat_score = 0;
    c.steering.heading = a;
    // Children flash for 300 ms before they can move or deal damage (§7.2.5).
    c.telegraph_until = w.tick + ticksOf(300);
  }
}

function expireOwnedZones(w: World, ownerId: number): void {
  const list = w.hazards.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const h = w.hazards.slots[list[i]];
    if (!h.alive || h.owner_id !== ownerId) continue;
    if (h.hazard_id !== HAZARD_NETTLE_ZONE) continue;
    h.end_tick = w.tick;
    w.bus.emit('hazard', { hazard_id: h.hazard_id, kind: 'end', pos: h.pos, radius: h.zone.r });
    w.hazards.release(h);
    if (liveZones > 0) liveZones--;
  }
}

/** Visual-only hazard for the §7.5 volatile ring; damage is applied by the burst queue. */
function spawnVolatileTelegraph(w: World, x: number, y: number, fireTick: number, ownerId: number): void {
  const h = w.hazards.acquire();
  if (!h) return;
  h.id = w.allocId();
  h.kind = 'hazard';
  h.faction = 'enemy';
  h.pos.x = x;
  h.pos.y = y;
  h.vel.x = 0;
  h.vel.y = 0;
  h.hazard_id = HAZARD_VOLATILE_RING;
  h.zone.shape = 'circle';
  h.zone.r = VOLATILE_RADIUS_U;
  h.zone.w = 0;
  h.zone.h = 0;
  h.hp = 1;
  h.max_hp = 1;
  h.hitbox_radius = VOLATILE_RADIUS_U;
  h.targetable = false;
  h.status_count = 0;
  h.spawn_tick = w.tick;
  h.damage_per_s = 0;
  h.status_id = '';
  h.owner_id = ownerId;
  h.telegraph_until = fireTick;
  h.arm_tick = fireTick;
  h.end_tick = fireTick + 6;
  w.bus.emit('hazard', { hazard_id: HAZARD_VOLATILE_RING, kind: 'telegraph', pos: h.pos, radius: VOLATILE_RADIUS_U });
}

function processBursts(w: World): void {
  let i = 0;
  while (i < burstCount) {
    if (w.tick < bTick[i]) {
      i++;
      continue;
    }
    if (bKind[i] === BURST_RING) {
      const dx = wrapDeltaX(w.player.pos.x - bX[i]);
      const dy = wrapDeltaY(w.player.pos.y - bY[i]);
      const rr = bR[i] + w.player.hitbox_radius;
      if (dx * dx + dy * dy <= rr * rr) dealPlayerDamage(w, bOwner[i], 'affix.volatile', bDmg[i]);
    } else {
      for (let k = 0; k < BRISTLING_DARTS; k++) {
        const a = (k / BRISTLING_DARTS) * TAU;
        spawnEnemyProjectile(
          w,
          bOwner[i],
          'affix.bristling',
          bX[i] + Math.cos(a) * 0.4,
          bY[i] + Math.sin(a) * 0.4,
          Math.cos(a) * BRISTLING_DART_SPEED,
          Math.sin(a) * BRISTLING_DART_SPEED,
          bDmg[i],
          0.25,
          180,
          '',
          VISUAL_ENEMY_DART,
          BEHAVIOUR_ENEMY_LINEAR
        );
      }
    }
    burstCount--;
    bKind[i] = bKind[burstCount];
    bX[i] = bX[burstCount];
    bY[i] = bY[burstCount];
    bR[i] = bR[burstCount];
    bDmg[i] = bDmg[burstCount];
    bTick[i] = bTick[burstCount];
    bOwner[i] = bOwner[burstCount];
  }
}

// --- enemy projectiles ---

function spawnEnemyProjectile(
  w: World,
  ownerId: number,
  ownerDefId: string,
  x: number,
  y: number,
  vx: number,
  vy: number,
  damage: number,
  radius: number,
  lifeTicks: number,
  statusId: string,
  visualClass: number,
  behaviour: number
): Projectile | null {
  // Muzzle offsets push past the seam; every position handed on must be canonical (AMD-24).
  const mx = wrapX(x);
  const my = wrapY(y);
  if (projSpawner) {
    return projSpawner(w, ownerId, ownerDefId, mx, my, vx, vy, damage, radius, lifeTicks, statusId, visualClass, behaviour);
  }
  const p = w.projectiles.acquire();
  if (!p) {
    w.bus.emit('pool_exhausted', { pool: 'projectiles', denied_kind: ownerDefId });
    return null;
  }
  p.id = w.allocId();
  p.kind = 'projectile';
  p.faction = 'enemy';
  p.pos.x = mx;
  p.pos.y = my;
  p.vel.x = vx;
  p.vel.y = vy;
  p.hp = 1;
  p.max_hp = 1;
  p.hitbox_radius = radius;
  p.targetable = false;
  p.status_count = 0;
  p.spawn_tick = w.tick;
  p.spawn_tick_f = w.tick;
  p.weapon_id = ownerDefId;
  p.owner_id = ownerId;
  p.damage_snapshot = damage;
  p.crit_snapshot = false;
  p.area_snapshot = radius;
  p.knockback_snapshot = 0;
  p.penetration_left = 0;
  p.ricochet_left = 0;
  p.expire_tick = w.tick + lifeTicks;
  p.hit_count = 0;
  p.behaviour = behaviour;
  p.phase = 0;
  p.orbit_radius = 0;
  p.homing_strength = 0;
  p.target_id = 0;
  p.status_on_hit = statusId;
  p.visual_class = visualClass;
  ownedProj[p.pool_index] = 1;
  return p;
}

/**
 * Integrates the enemy projectiles this module owns. Skipped entirely once R2
 * installs its spawner via `setEnemyProjectileSpawner` — R2 then owns them.
 */
function stepEnemyProjectiles(w: World): void {
  const list = w.projectiles.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const idx = list[i];
    if (ownedProj[idx] === 0) continue;
    const p = w.projectiles.slots[idx];
    // A slot recycled by R2 is no longer ours; the faction check makes that safe.
    if (!p.alive || p.faction !== 'enemy') {
      ownedProj[idx] = 0;
      continue;
    }
    p.pos.x += p.vel.x * STEP;
    p.pos.y += p.vel.y * STEP;
    wrapPos(p.pos);
    const dx = wrapDeltaX(p.pos.x - w.player.pos.x);
    const dy = wrapDeltaY(p.pos.y - w.player.pos.y);
    const rr = p.hitbox_radius + w.player.hitbox_radius;
    if (dx * dx + dy * dy <= rr * rr) {
      dealPlayerDamage(w, p.owner_id, p.weapon_id, p.damage_snapshot);
      ownedProj[idx] = 0;
      w.projectiles.release(p);
      continue;
    }
    // Nothing leaves a torus: `expire_tick` (already sized from max_range_u / speed) is the
    // only despawn, so a shot crossing the seam keeps its remaining range.
    if (w.tick >= p.expire_tick) {
      ownedProj[idx] = 0;
      w.projectiles.release(p);
    }
  }
}

export { applyTurnLimit };
